<template>
  <button v-if="repName" class="rep-offer-card" type="button">
    <span class="rep-offer-card__watermark" aria-hidden="true">
      <span class="rep-offer-card__watermark-badge">In Progress</span>
    </span>

    <span class="rep-offer-card__body" inert>
      <span class="rep-offer-card__info">
        <span class="rep-offer-card__eyebrow">Selling rep</span>
        <span class="rep-offer-card__name">{{ repName }}</span>
      </span>
      <span class="rep-offer-card__pill">Offer</span>
    </span>
  </button>
  <span v-else />
</template>

<script setup>
// The selling representative's offer tile, beside the current bid.
//
// It is deliberately UNFINISHED and says so: the interior is blurred and made
// `inert`, with an "In Progress" watermark over it. The offer flow does not
// exist yet, and a tile that looked live would invite a clerk to press it
// during a sale. It holds its place in the grid because the console's layout is
// matched position-for-position to the incumbent software, and leaving a hole
// there would move everything else.
//
// Renders nothing at all when no rep is assigned — an empty tile would read as
// "no offer yet" rather than "this lot has no rep".
defineOptions({ name: 'RepOfferCard' });

defineProps({
  // The selling rep's name, or null when the lot has none.
  repName: { type: String, default: null },
});
</script>

<style scoped>
.rep-offer-card {
  flex: 1;
  min-height: 0;
  min-width: 0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  font-family: inherit;
  background: var(--p-rep-offer-card-background);
  border: var(--p-rep-offer-card-border-width) solid var(--p-rep-offer-card-border-color);
  border-radius: var(--p-rep-offer-card-border-radius);
  padding: var(--p-rep-offer-card-padding);
}

.rep-offer-card__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  filter: blur(0.75px);
  opacity: 0.65;
  user-select: none;
  gap: var(--p-rep-offer-card-body-gap);
}

.rep-offer-card__watermark {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.rep-offer-card__watermark-badge {
  text-transform: uppercase;
  padding: var(--p-rep-offer-card-watermark-badge-padding);
  border-radius: var(--p-rep-offer-card-watermark-badge-border-radius);
  background: var(--p-rep-offer-card-watermark-badge-background);
  border: var(--p-rep-offer-card-watermark-badge-border-width) solid
    var(--p-rep-offer-card-watermark-badge-border-color);
  box-shadow: var(--p-rep-offer-card-watermark-badge-box-shadow);
  font-size: var(--p-rep-offer-card-watermark-badge-font-size);
  font-weight: var(--p-rep-offer-card-watermark-badge-font-weight);
  letter-spacing: var(--p-rep-offer-card-watermark-badge-letter-spacing);
  color: var(--p-rep-offer-card-watermark-badge-color);
}

.rep-offer-card__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
  min-width: 0;
}

.rep-offer-card__eyebrow {
  text-transform: uppercase;
  font-size: var(--p-rep-offer-card-eyebrow-font-size);
  font-weight: var(--p-rep-offer-card-eyebrow-font-weight);
  letter-spacing: var(--p-rep-offer-card-eyebrow-letter-spacing);
  color: var(--p-rep-offer-card-eyebrow-color);
}

.rep-offer-card__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--p-rep-offer-card-name-font-size);
  font-weight: var(--p-rep-offer-card-name-font-weight);
  color: var(--p-rep-offer-card-name-color);
}

.rep-offer-card__pill {
  text-transform: uppercase;
  font-size: var(--p-rep-offer-card-pill-font-size);
  font-weight: var(--p-rep-offer-card-pill-font-weight);
  letter-spacing: var(--p-rep-offer-card-pill-letter-spacing);
  color: var(--p-rep-offer-card-pill-color);
  background: var(--p-rep-offer-card-pill-background);
  border-radius: var(--p-rep-offer-card-pill-border-radius);
  padding: var(--p-rep-offer-card-pill-padding);
}
</style>
